import React from 'react';
import { X } from 'lucide-react';
import { C } from '../../theme.js';
import { Button, FieldLabel, IconButton, TextInput } from '../../components/Primitives.jsx';
import { ColSelect } from '../shared/ColSelect.jsx';

export function SplitColumnInspector({ step, onChange, uploadedRows }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  // One text box per output column, not a single comma-joined field — that
  // shape made it impossible to actually type a 2nd/3rd name, since every
  // keystroke re-split/trimmed/filtered the string and snapped the visible
  // text back, eating the delimiter the user had just typed.
  const names = (c.into || []).length ? c.into : [''];
  const setNames = (next) => u({ into: next });
  const updateName = (i, v) => setNames(names.map((n, j) => (j === i ? v : n)));
  const addName = () => setNames([...names, '']);
  const removeName = (i) => setNames(names.length > 1 ? names.filter((_, j) => j !== i) : ['']);

  // How many pieces the delimiter actually produces for this column, over
  // the rows arriving at this step (same split logic as s_split_column in
  // columns.py — a missing piece is filled with "", so letting the user add
  // more output boxes than this would just create all-blank columns).
  // null = unknown (no column/delimiter/data yet) — don't restrict.
  const maxParts = React.useMemo(() => {
    if (!c.column || !c.delim || !uploadedRows?.length) return null;
    // Same unescape as s_split_column: the tab chip stores backslash + t.
    const delim = c.delim.replace(/\\t/g, '\t').replace(/\\n/g, '\n');
    let max = 0;
    for (const r of uploadedRows) {
      const n = String(r?.[c.column] || '').split(delim).length;
      if (n > max) max = n;
    }
    return max > 0 ? max : null;
  }, [c.column, c.delim, uploadedRows]);

  // If the column/delimiter changes and the configured output names now
  // exceed what the data can actually produce, trim the excess instead of
  // leaving boxes that only ever fill with "".
  React.useEffect(() => {
    if (maxParts != null && names.length > maxParts) {
      setNames(names.slice(0, maxParts));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxParts]);

  const atMax = maxParts != null && names.length >= maxParts;

  return (
    <div>
      <FieldLabel>Source column</FieldLabel>
      <ColSelect value={c.column} onChange={(v) => u({ column: v })} />
      <FieldLabel>Delimiter</FieldLabel>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <TextInput mono value={c.delim} onChange={(v) => u({ delim: v })} placeholder="," style={{ flex: '0 0 80px' }} />
        {[',', ';', '|', '\\t', ' '].map(d => (
          <button key={d} onClick={() => u({ delim: d })}
            style={{
              fontFamily: "'JetBrains Mono', monospace", cursor: 'pointer',
              background: C.input, color: C.fg3,
              border: `1px solid ${C.border}`, borderRadius: 6,
              padding: '4px 9px', fontSize: '.74rem',
            }}>{d}</button>
        ))}
      </div>
      <FieldLabel hint={maxParts != null
        ? `Splitting '${c.column}' on this delimiter produces at most ${maxParts} piece${maxParts === 1 ? '' : 's'} here — one box per piece, no more can be added`
        : 'One box per piece the delimiter splits the column into — add a box for each new column you want'}>Output column names</FieldLabel>
      {names.map((name, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
          <span style={{ color: C.fg5, fontSize: '.7rem', fontFamily: "'JetBrains Mono', monospace", width: 18 }}>{i + 1}</span>
          <TextInput mono value={name} onChange={(v) => updateName(i, v)} placeholder={i === 0 ? 'local_part' : 'domain'} style={{ flex: 1 }} />
          <IconButton title="Remove" onClick={() => removeName(i)}><X size={14} /></IconButton>
        </div>
      ))}
      {!atMax && <Button kind="ghost" size="sm" onClick={addName} icon="＋">Add output column</Button>}
      {atMax && (
        <div style={{ color: C.fg5, fontSize: '.7rem', marginTop: 2 }}>
          Already at the maximum of {maxParts} column{maxParts === 1 ? '' : 's'} for this delimiter.
        </div>
      )}
    </div>
  );
}
